import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import Image from "react-bootstrap/Image";
import Select from 'react-select';
import { EditIcon } from "../../../components/SVGIcon";
import { useState, useEffect, useContext } from "react";
import { AppContext } from "../../../contexts/AppContext";
import { getAllCountries, getStatesofSelectedCountry, getCitiesofSelectedState } from "../../../services/countryStateService";
import { singleUser, updateCompanyDetails, updateProfilePic, updateUserstatus } from "../../../services/userService";
import { useAlert } from "../../../contexts/AlertContext";
import UserStatusDropDown from "../../../components/UserStatusDropDown";
import * as Yup from "yup";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { getAllTimezones } from "countries-and-timezones";
import { Country } from 'country-state-city';

const validationSchema = Yup.object({
  companyName: Yup.string().trim().required("Company name is required"),
  phoneNo: Yup.string()
    .trim()
    .required("Phone is required")
    .test('is-valid-phone', 'Invalid phone number', function (value) {
      if (!value) return false;
      const phoneNumber = parsePhoneNumberFromString(value);
      return phoneNumber ? phoneNumber.isValid() : false;
    }),
  address: Yup.string().trim().required("Street name is required"),
  country: Yup.string().required("Country is required"),
  state: Yup.string().required("State is required"),
  city: Yup.string().required("City is required"),
  zipCode: Yup.string().trim().required("Zip code is required"),
  timezone: Yup.string().required("Timezone is required"),
});

const CompanyDetails = ({ auth, setAuth }) => {
  const { setProfilePic } = useContext(AppContext);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryList, setCountryList] = useState([]);
  const [userData, setUserData] = useState([]);
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [stateList, setStateList] = useState([]);
  const [phoneDetails, setPhoneDetails] = useState(null);
  const [phoneNo, setPhoneNo] = useState("");
  const [selectedCity, setSelectedCity] = useState(null);
  const [cityList, setCityList] = useState([]);
  const { showAlert } = useAlert();
  const [userTemData, setUserTemData] = useState({});

  const [formData, setFormData] = useState({
    profilePic: "",
    companyName: "",
    email: "",
    phoneNo: "",
    address: "",
    country: "",
    state: "",
    city: "",
    zipCode: "",
    timezone: "",
  });

  useEffect(() => {
    const tzData = getAllTimezones();
    const tzArray = Object.values(tzData).map((tz) => {
      const countryCode = tz.countries?.[0];
      const countryName = countryCode ? Country.getCountryByCode(countryCode).name : "Unknown";
      return {
        value: tz.name,
        label: `${countryName} - ${tz.name} (GMT${tz.utcOffsetStr})`,
      };
    });
    tzArray.sort((a, b) => a.label.localeCompare(b.label));
    setTimezoneOptions(tzArray);

    singleUser(auth?.user?.id).then((response) => {
      if (response?.data?.user) {
        const tempUserData = response?.data?.user;

        const country = tempUserData?.country?.code
          ? { label: tempUserData.country.name, value: tempUserData.country.code }
          : null;
        const state = tempUserData?.state?.code
          ? { label: tempUserData.state.name, value: tempUserData.state.code }
          : null;
        const city = tempUserData?.city
          ? { label: tempUserData.city, value: tempUserData.city }
          : null;

        setSelectedCountry(country);
        setSelectedState(state);
        setSelectedCity(city);

        changePhoneNumber(
          (tempUserData?.countryCode?.startsWith('+')
            ? tempUserData.countryCode
            : '+' + tempUserData.countryCode) + tempUserData.phoneNo || ""
        );

        if (tempUserData?.timezone) {
          const match = tzArray.find((opt) => opt.value === tempUserData.timezone);
          if (match) setSelectedTimezone(match);
        }

        setFormData({
          profilePic: tempUserData?.profilePic
            ? tempUserData.profilePic
            : `https://ui-avatars.com/api/?name=${tempUserData?.firstName}+${tempUserData?.lastName}&background=007bff&color=fff&size=100`,
          companyName: tempUserData?.companyName || "",
          email: tempUserData?.email || "",
          phoneNo:
            (tempUserData.countryCode?.startsWith('+')
              ? tempUserData.countryCode
              : '+' + tempUserData.countryCode) + tempUserData.phoneNo || "",
          address: tempUserData?.address || "",
          country: tempUserData?.country?.code || "",
          state: tempUserData?.state?.code || "",
          city: tempUserData?.city || "",
          zipCode: tempUserData?.zipCode || "",
          timezone: tempUserData?.timezone || "",
        });
        setUserData(tempUserData);
      }
    });
  }, []);

  useEffect(() => {
    getAllCountries().then((response) => {
      if (response) {
        const priorityCodes = ['US', 'BR'];
        const priorityCountries = response.filter(country =>
          priorityCodes.includes(country.isoCode)
        );
        const otherCountries = response
          .filter(country => !priorityCodes.includes(country.isoCode))
          .sort((a, b) => a.name.localeCompare(b.name));
        const sortedList = [...priorityCountries, ...otherCountries];
        const options = sortedList.map(country => ({
          label: country.flag + ' ' + country.name,
          value: country.isoCode
        }));
        setCountryList(options);
      }
    });
  }, []);

  useEffect(() => {
    if (selectedCountry?.value) {
      getStatesofSelectedCountry(selectedCountry?.value).then((response) => {
        if (response) {
          const options = response.map(country => ({
            label: country.name,
            value: country.isoCode
          }));
          setStateList(options);
        }
      });
    } else {
      setStateList([]);
      setSelectedState(null);
      setCityList([]);
      setSelectedCity(null);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedState?.value) {
      getCitiesofSelectedState(selectedCountry?.value, selectedState?.value).then((response) => {
        if (response) {
          const options = response.map(country => ({
            label: country.name,
            value: country.name
          }));
          setCityList(options);
        }
      });
    } else {
      setCityList([]);
      setSelectedCity(null);
    }
  }, [selectedState]);

  const handleLocationChange = (type, newValue, setFieldValue) => {
    if (type === 'country') {
      setSelectedCountry(newValue);
      setSelectedState(null);
      setSelectedCity(null);
      setStateList([]);
      setCityList([]);
      setFieldValue('country', newValue?.value || '');
      setFieldValue('state', '');
      setFieldValue('city', '');
    } else if (type === 'state') {
      setSelectedState(newValue);
      setSelectedCity(null);
      setCityList([]);
      setFieldValue('state', newValue?.value || '');
      setFieldValue('city', '');
    } else {
      setSelectedCity(newValue);
      setFieldValue('city', newValue?.value || '');
    }
  };

  const handleFormSubmit = async (values) => {
    const [countryCode, phoneNo] = values.phoneNo.split(' ');
    const companyDetails = {
      companyId: userData.id,
      companyName: values.companyName,
      email: values.email,
      phoneNo: phoneDetails?.nationalNumber,
      countryCode: phoneDetails?.countryCallingCode,
      address: values.address,
      country: { name: selectedCountry?.label, code: selectedCountry?.value },
      state: { name: selectedState?.label, code: selectedState?.value },
      city: selectedCity?.value,
      zipCode: values.zipCode,
      userType: values.userType,
      timezone: values.timezone
    };

    updateCompanyDetails(companyDetails).then((res) => {
      if (res?.message) {
        setAuth({ ...auth, "user": { ...auth.user, companyName: res?.data?.companyName } })
      }
      showAlert({
        title: "Users Notification",
        message: res?.message ?? "something went wrong, try again later",
        variant: "success",
      });
    }).catch((error) =>
      showAlert({
        title: "Users Notification",
        message: error?.data?.message ?? "something went wrong, try again later",
        variant: "danger",
      })
    );
  };

  const changePhoneNumber = (value) => {
    if (typeof value !== "undefined") {
      const phoneDetailsTemp = parsePhoneNumber(value);
      setPhoneDetails(phoneDetailsTemp);
      setPhoneNo(value);
    }
  };

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("user_id", userData.id);

      updateProfilePic(userData.id, formData)
        .then((res) => {
          if (res?.data?.profilePic) {
            setAuth({ ...auth, "user": { ...auth.user, profilePic: res.data.profilePic } })
            setFormData(prev => ({ ...userTemData, profilePic: res.data.profilePic }));
          }

          showAlert({
            title: "Users Notification",
            message: res?.data?.message ?? "Profile photo updated successfully",
            variant: "success",
          });
        })
        .catch((error) =>
          showAlert({
            title: "Users Notification",
            message: error?.message ?? "Upload failed",
            variant: "danger",
          })
        );
    }
  };

  return (
    <>
      <div className="p-4">
        <h2 className="fs-5 fw-semibold text-gray-800 mb-4">
          Profile Photo
        </h2>
        <div className="d-flex align-items-center gap-4">
          <div className="user-profile-box ratio ratio-1x1">
            <Image
              className="rounded-pill object-fit-cover"
              src={formData?.profilePic}
              width={120}
              height={120}
              alt="User Photo"
            />
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="btn-file">
              <input type="file" id="btn-file-input" onChange={handleProfilePicChange} hidden />
              <label
                htmlFor="btn-file-input"
                className="btn btn-md bg-blue-100 border-0 text-blue-600 shadow-none d-flex align-items-center gap-2"
              >
                <EditIcon /> Edit Photo
              </label>
            </div>
          </div>
        </div>
      </div>

      <Formik
        initialValues={formData}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({ setFieldValue, handleChange, errors, values }) => (
          setUserTemData(values),
          <FormikForm>
            <div className="input-group-item input-group-warpper p-4 mt-3">
              <Row className="gy-4">
                <Form.Group className="col-md-12" controlId="companyName">
                  <Form.Label className="required">Company Name</Form.Label>
                  <Field
                    as={Form.Control}
                    name="companyName"
                    placeholder="Enter company name"
                  />
                  <ErrorMessage name="companyName">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group>

                <Form.Group className="col-md-6" controlId="email">
                  <Form.Label className="required">Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={values?.email}
                    placeholder="Enter email"
                    disabled
                  />
                </Form.Group>

                <Form.Group className="col-md-6" controlId="phoneNo">
                  <Form.Label className="required">Phone</Form.Label>
                  <PhoneInput
                    className="form-control"
                    international
                    name="phoneNo"
                    value={phoneNo}
                    onChange={(e) => {
                      if (typeof e !== "undefined") {
                        changePhoneNumber(e);
                        setFieldValue("phoneNo", e);
                      }
                    }}
                    placeholder="Enter phone number"
                  />
                  <ErrorMessage name="phoneNo">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group>

                <Form.Group className="col-md-12" controlId="address">
                  <Form.Label className="required">Street Name</Form.Label>
                  <Field
                    as={Form.Control}
                    name="address"
                    placeholder="Enter street name"
                  />
                  <ErrorMessage name="address">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group>

                <Form.Group className="col-md-6" controlId="country">
                  <Form.Label>Country</Form.Label>
                  <Select
                    className="react-form-control"
                    classNamePrefix="react-control"
                    options={countryList}
                    value={selectedCountry}
                    onChange={(e) => handleLocationChange("country", e, setFieldValue)}
                    placeholder="Select a country"
                  />
                  <Field type="hidden" name="country" value={selectedCountry?.value || ''} />
                  <ErrorMessage name="country">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group>

                <Form.Group className="col-md-6" controlId="state">
                  <Form.Label className="required">State</Form.Label>
                  <Select
                    className="react-form-control"
                    classNamePrefix="react-control"
                    options={stateList}
                    value={selectedState}
                    onChange={(e) => handleLocationChange("state", e, setFieldValue)}
                    placeholder="Select a state"
                    isDisabled={!selectedCountry}
                  />
                  <Field type="hidden" name="state" value={selectedState?.value || ''} />
                  <ErrorMessage name="state">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group>

                <Form.Group className="col-md-6" controlId="city">
                  <Form.Label className="required">City</Form.Label>
                  <Select
                    className="react-form-control"
                    classNamePrefix="react-control"
                    options={cityList}
                    value={selectedCity}
                    onChange={(e) => handleLocationChange("city", e, setFieldValue)}
                    placeholder="Select a city"
                    isDisabled={!selectedState}
                  />
                  <Field type="hidden" name="city" value={selectedCity?.value || ''} />
                  <ErrorMessage name="city">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group>

                <Form.Group className="col-md-6" controlId="zipCode">
                  <Form.Label className="required">Zip Code</Form.Label>
                  <Field
                    as={Form.Control}
                    name="zipCode"
                    type="text"
                    placeholder="Enter zip code"
                  />
                  <ErrorMessage name="zipCode">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group>

                <Form.Group className="col-md-6" controlId="timezone">
                  <Form.Label className="required">Timezone</Form.Label>
                  <Select
                    className="react-form-control"
                    classNamePrefix="react-control"
                    options={timezoneOptions}
                    value={selectedTimezone}
                    onChange={(e) => {
                      setFieldValue("timezone", e.value);
                      setSelectedTimezone(e);
                    }}
                    placeholder="Select a timezone"
                  />
                  <Field type="hidden" name="timezone" />
                  <ErrorMessage name="timezone">
                    {(msg) => <Form.Text className="text-danger">{msg}</Form.Text>}
                  </ErrorMessage>
                </Form.Group>
              </Row>
            </div>

            <div className="input-group-warpper d-flex align-align-items-center gap-2 p-4 mt-3">
              <Button variant="primary" className="btn-md" type="submit">Save & Update</Button>
            </div>
          </FormikForm>
        )}
      </Formik>
    </>
  );
};

export default CompanyDetails;